import type {
  Asset,
  CascadeNode,
  DependencyEdge,
  RelationType,
  ScenarioInput,
  ScenarioOutput,
  Severity,
} from '@/types';

/**
 * Per-relation propagation behaviour. Each entry tunes how a failure on the
 * "from" side of an edge affects the "to" side.
 *
 * `attenuation`: multiplier applied to the upstream loss fraction. 1.0 means
 *   full pass-through; 0.5 means the downstream feels half the impact.
 * `extra_seconds`: additional latency layered on top of the edge's
 *   propagation_seconds (e.g. supply chains have inertia even after the
 *   nominal lead time).
 * `severity_floor`: never report below this severity for downstream nodes.
 */
const RELATION_RULES: Record<
  RelationType,
  { attenuation: number; extra_seconds: number; severity_floor: Severity }
> = {
  feeds: { attenuation: 1.0, extra_seconds: 0, severity_floor: 4 },
  depends_on: { attenuation: 1.0, extra_seconds: 0, severity_floor: 4 },
  controls: { attenuation: 0.85, extra_seconds: 0, severity_floor: 3 },
  communicates_with: { attenuation: 0.55, extra_seconds: 5, severity_floor: 2 },
  shares_corridor: { attenuation: 0.4, extra_seconds: 0, severity_floor: 2 },
  supplies: { attenuation: 0.7, extra_seconds: 1800, severity_floor: 3 },
  backs_up: { attenuation: 0.25, extra_seconds: 60, severity_floor: 2 },
  regulates: { attenuation: 0.05, extra_seconds: 86400, severity_floor: 1 },
  insures: { attenuation: 0.0, extra_seconds: 0, severity_floor: 1 },
};

const ASSET_TYPE_RECOVERY_HOURS: Record<string, number> = {
  power_plant: 24,
  substation: 12,
  transmission_line: 6,
  distribution_network: 4,
  pipeline: 168,
  refinery: 96,
  port: 72,
  terminal: 48,
  mine: 168,
  processing_plant: 96,
  water_treatment: 24,
  wastewater: 12,
  dam: 720,
  reservoir: 720,
  data_centre: 6,
  telecom_tower: 24,
  telecom_exchange: 12,
  manufacturing_facility: 72,
  solar_farm: 12,
  wind_farm: 24,
  hydroelectric: 48,
  nuclear: 720,
  gas_storage: 168,
  lng_terminal: 96,
  rail_network: 24,
  road_bridge: 720,
  tunnel: 720,
  airport: 24,
  military_installation: 72,
  government_facility: 24,
  submarine_cable: 720,
  subsea_pipeline: 720,
  other: 48,
};

function severityFromLoss(loss: number): Severity {
  if (loss >= 0.9) return 5;
  if (loss >= 0.7) return 4;
  if (loss >= 0.4) return 3;
  if (loss >= 0.15) return 2;
  return 1;
}

interface CascadeOptions {
  /** Stop propagation when downstream loss falls below this threshold. */
  loss_threshold?: number;
  /** Maximum BFS hops. */
  max_depth?: number;
  /** If true, treat assets with `has_backup === true` as recoverable; halves loss factor. */
  honour_backups?: boolean;
}

/**
 * Run the cascade engine. Performs a BFS over the dependency graph from the
 * initial failed asset set, accumulating loss fractions and time-to-impact.
 */
export function runCascade(
  scenario: ScenarioInput,
  assets: Asset[],
  edges: DependencyEdge[],
  options: CascadeOptions = {},
): ScenarioOutput {
  const { loss_threshold = 0.05, max_depth = 8, honour_backups = true } = options;

  const assetById = new Map<string, Asset>();
  for (const a of assets) assetById.set(a.asset_id, a);

  // adjacency: from -> [edges]
  const out = new Map<string, DependencyEdge[]>();
  for (const e of edges) {
    if (!out.has(e.from_asset_id)) out.set(e.from_asset_id, []);
    out.get(e.from_asset_id)!.push(e);
  }

  type Frame = {
    asset_id: string;
    affected_at_seconds: number;
    loss_fraction: number;
    path: string[];
    depth: number;
    reason: string;
  };

  const visited = new Map<string, CascadeNode>();
  const queue: Frame[] = [];

  for (const root of scenario.initial_failed_assets) {
    queue.push({
      asset_id: root,
      affected_at_seconds: 0,
      loss_fraction: 1.0,
      path: [root],
      depth: 0,
      reason: scenarioReason(scenario.mode),
    });
  }

  while (queue.length > 0) {
    const cur = queue.shift()!;
    const existing = visited.get(cur.asset_id);
    if (existing) {
      // Update if this path arrived earlier or with greater loss
      if (
        cur.affected_at_seconds < existing.affected_at_seconds ||
        cur.loss_fraction > existing.loss_fraction
      ) {
        existing.affected_at_seconds = Math.min(existing.affected_at_seconds, cur.affected_at_seconds);
        existing.loss_fraction = Math.max(existing.loss_fraction, cur.loss_fraction);
        existing.severity = severityFromLoss(existing.loss_fraction);
        if (!existing.propagation_path.includes(cur.path[0]!)) {
          existing.propagation_path = mergePaths(existing.propagation_path, cur.path);
        }
      }
    } else {
      visited.set(cur.asset_id, {
        asset_id: cur.asset_id,
        affected_at_seconds: cur.affected_at_seconds,
        loss_fraction: cur.loss_fraction,
        severity: severityFromLoss(cur.loss_fraction),
        reason: cur.reason,
        propagation_path: [...cur.path],
      });
    }

    if (cur.depth >= max_depth) continue;
    const outgoing = out.get(cur.asset_id) || [];
    for (const edge of outgoing) {
      const rule = RELATION_RULES[edge.relation];
      let nextLoss = cur.loss_fraction * edge.loss_factor * rule.attenuation;
      if (honour_backups && edge.has_backup) nextLoss *= 0.5;
      if (nextLoss < loss_threshold) continue;

      const nextSeconds =
        cur.affected_at_seconds + edge.propagation_seconds + rule.extra_seconds;
      const reason = humanReason(edge);
      queue.push({
        asset_id: edge.to_asset_id,
        affected_at_seconds: nextSeconds,
        loss_fraction: nextLoss,
        path: [...cur.path, edge.to_asset_id],
        depth: cur.depth + 1,
        reason,
      });
    }
  }

  // Lift severity floor by relation rule for non-root nodes
  for (const node of visited.values()) {
    if (scenario.initial_failed_assets.includes(node.asset_id)) {
      node.severity = 5;
      node.loss_fraction = 1.0;
    }
  }

  const affected = Array.from(visited.values()).sort(
    (a, b) => a.affected_at_seconds - b.affected_at_seconds,
  );

  return {
    scenario_id: scenario.scenario_id,
    affected,
    total_assets_impacted: affected.length,
    population_impacted_estimate: estimatePopulation(affected, assetById),
    economic_impact_eur_m: estimateEconomicImpact(affected, assetById),
    recovery_hours_estimate: estimateRecoveryHours(affected, assetById),
    confidence_intervals: computeConfidenceInterval(affected),
    computed_at: new Date().toISOString(),
  };
}

function mergePaths(a: string[], b: string[]): string[] {
  // Keep the shorter path (more direct cascade route)
  return a.length <= b.length ? a : b;
}

function scenarioReason(mode: ScenarioInput['mode']): string {
  switch (mode) {
    case 'failure':
      return 'Initial failure point';
    case 'attack_physical':
      return 'Physical attack target';
    case 'attack_cyber':
      return 'Cyber attack target';
    case 'attack_cyber_physical':
      return 'Coordinated cyber-physical attack target';
    case 'supply_chain':
      return 'Supply-chain compromise';
    case 'weather':
      return 'Weather impact';
    case 'demand_shock':
      return 'Demand shock';
    case 'geopolitical':
      return 'Geopolitical disruption';
  }
}

function humanReason(edge: DependencyEdge): string {
  switch (edge.relation) {
    case 'feeds':
      return `Loses upstream supply via ${edge.from_asset_id}`;
    case 'depends_on':
      return `Hard dependency on ${edge.from_asset_id}`;
    case 'controls':
      return `Control plane impacted via ${edge.from_asset_id}`;
    case 'communicates_with':
      return `Comms link impacted via ${edge.from_asset_id}`;
    case 'shares_corridor':
      return `Co-located corridor impact from ${edge.from_asset_id}`;
    case 'supplies':
      return `Supply-chain disruption from ${edge.from_asset_id}`;
    case 'backs_up':
      return `Lost redundancy partner ${edge.from_asset_id}`;
    case 'regulates':
      return `Regulatory disruption from ${edge.from_asset_id}`;
    case 'insures':
      return `Insurer impact from ${edge.from_asset_id}`;
  }
}

function estimatePopulation(
  nodes: CascadeNode[],
  byId: Map<string, Asset>,
): number {
  let total = 0;
  for (const n of nodes) {
    const a = byId.get(n.asset_id);
    if (!a) continue;
    const baseline = baselinePopulation(a);
    total += baseline * n.loss_fraction;
  }
  return Math.round(total);
}

function baselinePopulation(a: Asset): number {
  const sectorPop: Record<string, number> = {
    energy_generation: 250000,
    energy_transmission: 600000,
    energy_distribution: 200000,
    oil_gas: 100000,
    mining: 5000,
    water: 250000,
    wastewater: 300000,
    maritime_ports: 50000,
    telecommunications: 1500000,
    transport: 80000,
    manufacturing: 8000,
    data_centres: 500000,
    defence: 0,
    government: 1000000,
    other: 0,
  };
  const base = sectorPop[a.sector] ?? 1000;
  const critMultiplier = [0.2, 0.4, 0.7, 1.0, 1.6][a.criticality_rating - 1] ?? 1.0;
  return base * critMultiplier;
}

function estimateEconomicImpact(
  nodes: CascadeNode[],
  byId: Map<string, Asset>,
): number {
  let total = 0;
  for (const n of nodes) {
    const a = byId.get(n.asset_id);
    if (!a) continue;
    const sectorRate: Record<string, number> = {
      energy_generation: 14,
      energy_transmission: 28,
      energy_distribution: 6,
      oil_gas: 22,
      mining: 8,
      water: 4,
      wastewater: 2,
      maritime_ports: 22,
      telecommunications: 18,
      transport: 9,
      manufacturing: 11,
      data_centres: 30,
      defence: 0,
      government: 5,
      other: 1,
    };
    const rate = sectorRate[a.sector] ?? 4;
    const hoursAffected = Math.max(
      1,
      ASSET_TYPE_RECOVERY_HOURS[a.type] ?? 24,
    );
    total += rate * hoursAffected * n.loss_fraction * a.criticality_rating;
  }
  return Math.round(total);
}

function estimateRecoveryHours(
  nodes: CascadeNode[],
  byId: Map<string, Asset>,
): number {
  let max = 0;
  for (const n of nodes) {
    const a = byId.get(n.asset_id);
    if (!a) continue;
    const hours = (ASSET_TYPE_RECOVERY_HOURS[a.type] ?? 24) * n.loss_fraction;
    if (hours > max) max = hours;
  }
  return Math.round(max);
}

function computeConfidenceInterval(nodes: CascadeNode[]) {
  if (nodes.length === 0) return { p10: 0, p50: 0, p90: 0 };
  const losses = nodes.map((n) => n.loss_fraction).sort((a, b) => a - b);
  const idx = (q: number) => Math.floor((losses.length - 1) * q);
  return {
    p10: Math.round((losses[idx(0.1)] ?? 0) * 100),
    p50: Math.round((losses[idx(0.5)] ?? 0) * 100),
    p90: Math.round((losses[idx(0.9)] ?? 0) * 100),
  };
}
